// Multi-source reconciliation. Pure: given the successful vote results, pick a
// primary, compute margins, and flag divergence. It never picks a "winner"
// source silently — every source's margin is exposed in the strip, and a leader
// conflict is always surfaced regardless of magnitude.
import type { RaceConfig } from '../types/config.ts';
import type { NormalizedResult, CandidateResult } from '../types/results.ts';

export interface Margin {
  leaderId: string | null;
  runnerUpId: string | null;
  /** Leader minus runner-up in percentage points. null = unknown. */
  marginPct: number | null;
  marginVotes: number | null;
  reportingPercent: number | null;
}

export interface SourceStripItem {
  sourceId: string;
  label: string;
  margin: Margin;
  fetchedAt: string;
}

export interface Divergence {
  diverged: boolean;
  maxDeltaPct: number;
  /** Sources reporting a DIFFERENT leader than the primary — the strongest
   *  signal; flagged always, never auto-resolved. */
  leaderConflict: boolean;
  offenders: { sourceId: string; label: string; deltaPct: number; differentLeader: boolean }[];
}

export interface Reconciliation {
  primary: NormalizedResult | null;
  primaryMargin: Margin | null;
  strip: SourceStripItem[];
  divergence: Divergence;
}

const EMPTY_MARGIN: Margin = {
  leaderId: null,
  runnerUpId: null,
  marginPct: null,
  marginVotes: null,
  reportingPercent: null,
};

function sortKey(c: CandidateResult): number {
  if (c.votes != null) return c.votes;
  if (c.percent != null) return c.percent;
  return -Infinity;
}

/** Margin from a bare candidate list — shared by statewide results and counties. */
export function marginOfCandidates(
  candidates: CandidateResult[],
  reportingPercent: number | null,
): Margin {
  const cs = [...candidates].sort((a, b) => sortKey(b) - sortKey(a));
  const leader = cs[0];
  const runnerUp = cs[1];
  if (!leader) return { ...EMPTY_MARGIN, reportingPercent };

  const totalVotes = candidates.reduce((s, c) => s + (c.votes ?? 0), 0);
  let marginVotes: number | null = null;
  let marginPct: number | null = null;

  if (runnerUp) {
    if (leader.votes != null && runnerUp.votes != null) {
      marginVotes = leader.votes - runnerUp.votes;
    }
    if (leader.percent != null && runnerUp.percent != null) {
      marginPct = leader.percent - runnerUp.percent;
    } else if (marginVotes != null && totalVotes > 0) {
      marginPct = (marginVotes / totalVotes) * 100;
    }
  }

  return {
    leaderId: leader.id,
    runnerUpId: runnerUp?.id ?? null,
    marginPct,
    marginVotes,
    reportingPercent,
  };
}

export function computeMargin(r: NormalizedResult): Margin {
  return marginOfCandidates(r.candidates, r.reportingPercent);
}

function pickPrimary(results: NormalizedResult[], cfg: RaceConfig): NormalizedResult | null {
  if (!results.length) return null;
  const primaryId = cfg.voteProviders.find((p) => p.primary)?.id;
  const flagged = primaryId ? results.find((r) => r.sourceId === primaryId) : undefined;
  if (flagged) return flagged;
  const byReporting = [...results].sort(
    (a, b) => (b.reportingPercent ?? -1) - (a.reportingPercent ?? -1),
  );
  return byReporting[0] ?? results[0] ?? null;
}

export function reconcile(results: NormalizedResult[], cfg: RaceConfig): Reconciliation {
  const primary = pickPrimary(results, cfg);
  if (!primary) {
    return {
      primary: null,
      primaryMargin: null,
      strip: [],
      divergence: { diverged: false, maxDeltaPct: 0, leaderConflict: false, offenders: [] },
    };
  }

  const primaryMargin = computeMargin(primary);
  const others = results.filter((r) => r.sourceId !== primary.sourceId);

  const strip: SourceStripItem[] = others.map((r) => ({
    sourceId: r.sourceId,
    label: r.source,
    margin: computeMargin(r),
    fetchedAt: r.fetchedAt,
  }));

  const offenders: Divergence['offenders'] = [];
  let maxDeltaPct = 0;
  let leaderConflict = false;

  for (const item of strip) {
    const differentLeader =
      item.margin.leaderId != null &&
      primaryMargin.leaderId != null &&
      item.margin.leaderId !== primaryMargin.leaderId;
    if (differentLeader) leaderConflict = true;

    let deltaPct = 0;
    if (item.margin.marginPct != null && primaryMargin.marginPct != null) {
      deltaPct = Math.abs(item.margin.marginPct - primaryMargin.marginPct);
      maxDeltaPct = Math.max(maxDeltaPct, deltaPct);
    }
    if (differentLeader || deltaPct > cfg.divergenceFlagPct) {
      offenders.push({ sourceId: item.sourceId, label: item.label, deltaPct, differentLeader });
    }
  }

  return {
    primary,
    primaryMargin,
    strip,
    divergence: {
      diverged: maxDeltaPct > cfg.divergenceFlagPct || leaderConflict,
      maxDeltaPct,
      leaderConflict,
      offenders,
    },
  };
}
